import { UAParser } from "ua-parser-js";
import { Vault } from "../../libs/storage.js";
import { uuidv7 } from "./uuidv7";
import { reportAppEvent, reportStorageFallback } from "./CoreReport";
import { readProtoCtxExtension, PROTOCTX_VID_KEY } from "./Tracking";

export { User, AdditionalMessageData };

interface AdditionalMessageData {
  user_browser: string;
  user_browser_version: string;
  user_os: string;
  user_device: string;
  visitor_id: string;
}

// Claves que NUNCA deben persistir en el prefill. `event_id` es la identidad
// de UNA conversión concreta (píxeles de navegador): guardada 365 días se
// colaba en `dataLayer[0]` de todas las páginas siguientes —donde una etiqueta
// de GTM que lea el DLV `event_id` mandaría a la red un Event ID caducado y
// constante, y la red deduplicaría contra él— y en los tags del proveedor de
// push, que envía TODAS las claves de `u_form_data`. Se borra del MERGE, no
// solo de la entrada, para purgar también lo que ya hubiera guardado una
// versión anterior (el merge nunca elimina claves por sí solo).
const VolatileFormKeys = ["event_id"];

const StorageKeys = {
  visitorId: "t_visitor_id",
  visitorBrowserData: "t_visitor_browser_data",
  userFormData: "u_form_data",
};

class User {
  private _visitorId: string | null = null;
  private _browserData: UAParser.IResult | null = null;

  constructor() {}

  async init() {
    return await this.getClientBrowser();
  }

  getAdditionalDataToMessage(): AdditionalMessageData {
    // Con storage bloqueado/expirado Vault.get devuelve false y el deref de
    // data.browser.name reventaba generateMessage ANTES del fetch → lead
    // perdido (visto en prod 31/7/2026, funnel 15, iOS). Cascada de fallbacks:
    // Vault → copia en memoria (getClientBrowser la deja siempre) → recalcular
    // la UA al vuelo. Este getter no puede tumbar jamás el guardado del lead.
    let data = Vault.get(StorageKeys.visitorBrowserData) as
      | UAParser.IResult
      | false;
    if (!data || !data.browser) {
      reportStorageFallback("user", this._browserData ? "memory" : "recomputed");
      data = this._browserData || new UAParser(navigator.userAgent).getResult();
    }
    const visitorId = Vault.get(StorageKeys.visitorId);

    return {
      user_browser: (data.browser && data.browser.name) || "",
      user_browser_version: (data.browser && data.browser.major) || "",
      user_os: (data.os && data.os.name) || "",
      // device.type de UAParser: "mobile"/"tablet"/"smarttv"/...; undefined =
      // escritorio. El código histórico leía device.name — propiedad que
      // IDevice no tiene — así que TODO el tráfico se reportaba "desktop"
      // (bug detectado en analytics el 4/8/2026).
      user_device: (data.device && data.device.type) || "desktop",
      visitor_id: String(visitorId || this._visitorId || ""),
    };
  }

  getUserFormData(): Record<string, string> {
    // Nada de este getter puede lanzar (issue 1K): valores legados con
    // codificación antigua o storage corrupto devuelven {} y en paz.
    try {
      const userFormData = Vault.get(StorageKeys.userFormData);
      if (userFormData === false) {
        return {};
      }
      const raw = window.atob(userFormData);
      try {
        // Camino nuevo: base64(UTF-8 percent-encoded)
        return JSON.parse(decodeURIComponent(escape(raw)));
      } catch {
        // Valores legados escritos con btoa(JSON) plano (Latin1)
        return JSON.parse(raw);
      }
    } catch {
      return {};
    }
  }

  setUserFormData(data: Record<string, string>): void {
    // btoa lanza InvalidCharacterError con caracteres fuera de Latin1
    // (emojis, algunos acentos): issue 1K — el lead YA estaba guardado en SQS
    // y esta contabilidad lo convertía en error visible → el usuario reenviaba
    // → duplicados. Codificación unicode-safe + jamás lanzar hacia el caller.
    try {
      const current = this.getUserFormData();
      const merged: Record<string, string> = { ...current, ...data };
      for (const key of VolatileFormKeys) {
        delete merged[key];
      }
      Vault.set(
        StorageKeys.userFormData,
        window.btoa(unescape(encodeURIComponent(JSON.stringify(merged)))),
        { expires: "365d" },
      );
    } catch {
      // best-effort: perder el prefill no puede afectar al flujo del lead
    }
  }

  private async getClientBrowser() {
    try {
      const storedVisitorId = Vault.get(StorageKeys.visitorId);
      const storedBrowserData = Vault.get(StorageKeys.visitorBrowserData);

      // Identidad: Vault > vid del protoctx de la URL > acuñar nueva.
      // El vid lo pone decorateUrl en cada salto del funnel (serie 5/8/2026):
      // sin él, un usuario sin storage estrenaba visitor id EN CADA PÁGINA y
      // rompía dedupe + la resolución server-side de click IDs (CAPI), que
      // busca en el visitor-store por el id de la página de aterrizaje.
      // Con Vault sano se ignora la URL: la identidad persistida (365d) es
      // quien eres; la de la URL, solo un rescate para cuando no hay ninguna.
      // (Antes, si faltaba solo browserData, se regeneraba TAMBIÉN el id
      // existente — corregido: cada clave se repone por separado.)
      if (storedVisitorId === false) {
        const adopted = readAdoptableVisitorIdFromUrl();
        if (adopted !== "") {
          this._visitorId = adopted;
          reportAppEvent("vid_adopted", { app_phase: "user" });
        } else {
          this._visitorId = uuidv7();
        }
        Vault.set(StorageKeys.visitorId, this._visitorId, { expires: "365d" });
      } else {
        this._visitorId = storedVisitorId;
      }

      if (storedBrowserData === false) {
        this._browserData = new UAParser(navigator.userAgent).getResult();
        Vault.set(StorageKeys.visitorBrowserData, this._browserData, { expires: "365d" });
      } else {
        this._browserData = storedBrowserData;
      }
    } catch (error) {
      throw new Error(error);
    }

    return Promise.resolve();
  }
}

// Formato uuidv7 estricto (el mismo que acuña este módulo): nada que no
// cuadre se adopta — un valor basura en la URL jamás se convierte en
// identidad. Los ids numéricos legacy tampoco (solo viajan ids nuestros).
const UUIDV7_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

// Frescura máxima del vid adoptable — REINSTAURADO por la revisión
// adversarial del 6/8/2026: como las URLs de páginas 2+ se comparten
// (WhatsApp, comunidades, bookmarks), sin este guard cada receptor nuevo
// adoptaría el id del emisor y N personas colapsarían en un visitante (la
// CAPI atribuiría sus conversiones al click del emisor). El timestamp va
// embebido en el propio uuidv7: un salto real de funnel tarda minutos; un
// link compartido suele abrirse horas/días después. Una línea para quitarlo
// si el equipo prefiere adoptar sin límite.
const VID_ADOPTION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function readAdoptableVisitorIdFromUrl(): string {
  try {
    const candidate = readProtoCtxExtension(
      window.location.search,
      PROTOCTX_VID_KEY,
    );
    if (!UUIDV7_PATTERN.test(candidate)) {
      return "";
    }
    const mintedAtMs = parseInt(
      candidate.replace(/-/g, "").slice(0, 12),
      16,
    );
    const age = Date.now() - mintedAtMs;
    if (isNaN(age) || age < -300000 || age > VID_ADOPTION_MAX_AGE_MS) {
      return "";
    }
    return candidate;
  } catch {
    return "";
  }
}
